import { resolveBg } from "@/lib/bg-color";

export type Surface = {
  backgroundColor: string;
  onDark: boolean;
  accentClass: string;
};

function luminance(hex: string): number {
  const v = hex.replace("#", "");
  const full =
    v.length === 3
      ? v
          .split("")
          .map((c) => c + c)
          .join("")
      : v;
  const channel = (i: number) => {
    const c = parseInt(full.slice(i * 2, i * 2 + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

function contrast(a: number, b: number): number {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

const NAVY_03 = 0.0166; // luminance of #051d2d, the palette's darkest text colour

function ratioOn(hex: string, background: string): number {
  return contrast(luminance(hex), luminance(background));
}

// WCAG AA for normal text. 3:1 only covers large text and UI parts.
const MIN_ACCENT_CONTRAST = 4.5;

function accentFor(hex: string, onDark: boolean): string {
  if (onDark) {
    return ratioOn("#88ddd7", hex) >= MIN_ACCENT_CONTRAST ? "text-teal-04" : "text-white";
  }
  if (ratioOn("#00857c", hex) >= MIN_ACCENT_CONTRAST) return "text-teal-07";
  if (ratioOn("#00645e", hex) >= MIN_ACCENT_CONTRAST) return "text-teal-08";
  return "text-navy-03";
}

export function surfaceFrom(value: unknown): Surface | null {
  let hex: string | undefined;

  if (value && typeof value === "object" && "value" in value) {
    const raw = (value as { value?: unknown }).value;
    if (typeof raw === "string" && /^#[0-9a-f]{3,8}$/i.test(raw.trim())) {
      hex = raw.trim();
    }
  } else if (typeof value === "string" && value) {
    // Legacy slugs ("forest-03") still arrive from pre-rebrand content.
    hex = /^#/.test(value) ? value : resolveBg(value, "white").hex;
  }

  if (!hex) return null;

  const l = luminance(hex);
  const onDark = contrast(l, 1) > contrast(l, NAVY_03);
  return { backgroundColor: hex, onDark, accentClass: accentFor(hex, onDark) };
}

export function onSurface(
  surface: Surface | null | undefined,
  dark: string,
  light: string,
  fallback: string,
): string {
  if (!surface) return fallback;
  return surface.onDark ? dark : light;
}

export function accentOn(surface: Surface | null | undefined, fallback: string): string {
  return surface ? surface.accentClass : fallback;
}

export function headingOn(surface: Surface | null | undefined, fallback: string): string {
  if (!surface) return fallback;
  const hex = surface.backgroundColor;

  if (surface.onDark) {
    if (fallback === "text-white") return fallback;
    return ratioOn("#00bfb3", hex) >= MIN_ACCENT_CONTRAST ? "text-teal-05" : "text-white";
  }

  // White on a light ground is the one case that cannot stand.
  if (fallback !== "text-white" && luminance(hex) >= 0.97) return fallback;
  return ratioOn("#00645e", hex) >= MIN_ACCENT_CONTRAST ? "text-teal-08" : "text-navy-03";
}
